"use client";

import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import AuthService from "../../services/authService";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logo from "../../assets/LogoPdf.png";
import BackgroundImage from "../../assets/background.jpg";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setSnackbar({
        open: true,
        message: "Ingresá tu correo electrónico",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await AuthService.requestPasswordReset({ email: trimmed });
      setSnackbar({
        open: true,
        message:
          "Si el correo está registrado, te enviamos instrucciones para restablecer la contraseña.",
        severity: "success",
      });
      setEmail("");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "No pudimos procesar la solicitud. Intentá de nuevo más tarde.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f8f9fa",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: "#e0e0e0",
        transition: "border-color 0.2s ease",
      },
      "&:hover fieldset": {
        borderColor: "#b0b0b0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2563EB",
        borderWidth: "2px",
      },
      "&.Mui-focused": {
        backgroundColor: "#fff",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#888",
      "&.Mui-focused": {
        color: "#2563EB",
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          px: 6,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 420 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "#fff",
              mb: 2,
              letterSpacing: "-0.02em",
              textShadow: "0 2px 12px rgba(0,0,0,0.3)",
            }}
          >
            Recuperar acceso
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontWeight: 400,
              lineHeight: 1.6,
              textShadow: "0 1px 6px rgba(0,0,0,0.2)",
            }}
          >
            Te enviaremos un enlace para crear una nueva contraseña de forma segura.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: { xs: "100%", md: 480 },
          minWidth: { md: 480 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          backgroundColor: "rgba(255, 255, 255, 0.97)",
          backdropFilter: "blur(20px)",
          px: { xs: 3, sm: 5 },
          py: 4,
          boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 380 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img src={Logo} alt="Voras" style={{ height: 56 }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              color: "#1a1a1a",
              mb: 0.5,
            }}
          >
            ¿Olvidaste tu contraseña?
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "#888",
              mb: 3,
              fontSize: "0.9rem",
            }}
          >
            Escribí el correo asociado a tu cuenta y te contactamos con los pasos a seguir.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, ml: 0.5, display: "block" }}
            >
              Correo electrónico
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="tu@correo.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              sx={{ ...inputSx, mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: "#999", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              disableElevation
              sx={{
                borderRadius: "12px",
                padding: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                backgroundColor: "#2563EB",
                mb: 2,
                "&:hover": { backgroundColor: "#1D4ED8" },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Enviar instrucciones"}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Link
                component={RouterLink}
                to="/login"
                underline="hover"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#2563EB",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
                Volver al inicio de sesión
              </Link>
            </Box>
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            bottom: 20,
            color: "#bbb",
            fontSize: "0.7rem",
          }}
        >
          © {new Date().getFullYear()} Voras · Todos los derechos reservados
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 6000 : 8000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ForgotPassword;
